/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */





// ============================================================================
// PROMOTIONAL BLOCK - CUSTOM ELEMENT
// ============================================================================

class PromoBlockElement extends HTMLElement {
  connectedCallback() {
    // This fires automatically when the element is inserted into the DOM
    
    // Completely hide block initially - no layout shift, no flash
    this.style.display = 'none';
    this.style.opacity = '0';
    this.style.transition = 'opacity 0.3s ease-in-out';
    this.dataset.initialized = 'false';
    
    this.initDraggableScroll();
    
    // Process cart data and show block
    const processCartData = () => {
      // Mark as initialized
      this.dataset.initialized = 'true';
      
      // Remove the hidden class
      this.classList.remove('promo-block--hidden');
      
      // Show the block with smooth fade-in
      this.style.display = 'block';
      
      // Small delay to ensure display:block is applied before opacity transition
      requestAnimationFrame(() => {
        this.style.opacity = '1';
      });
    };
    
    // Check if there's recent cart data available (from a recent cart:updated event)
    if (window.recentCartData && Date.now() - window.recentCartDataTime < 1000) {
      // Use recent cart data if it's less than 1 second old
      this.updatePromoBlockWithCart(window.recentCartData);
      processCartData();
    } else {
      // Fetch cart data, then show block
      this.updatePromoBlock().then(processCartData).catch(() => {
        // Even on error, show the block
        processCartData();
      });
    }
    
    // Create bound methods to use the same reference for removal
    if (!this.boundCartUpdated) {
      this.boundCartUpdated = (event) => {
        // Store cart data for potential reuse
        if (event.detail && event.detail.cart) {
          window.recentCartData = event.detail.cart;
          window.recentCartDataTime = Date.now();
          this.updatePromoBlockWithCart(event.detail.cart);
          
          // If this is the first update and block isn't initialized, show it now
          if (this.dataset.initialized !== 'true') {
            this.dataset.initialized = 'true';
            this.classList.remove('promo-block--hidden');
            this.style.display = 'block';
            requestAnimationFrame(() => {
              this.style.opacity = '1';
            });
          }
        } else {
          // Add small delay for cart to be updated
          setTimeout(() => this.updatePromoBlock(), 50);
        }
      };
      
      this.boundCartRefresh = () => {
        // Add a small delay to ensure cart is updated
        setTimeout(() => this.updatePromoBlock(), 100);
      };
    }
    
    // Listen to cart events for updates
    document.addEventListener('cart:updated', this.boundCartUpdated);
    document.addEventListener('cart:refresh', this.boundCartRefresh);
  }
  
  disconnectedCallback() {
    // Clean up event listeners when element is removed
    if (this.boundCartUpdated) {
      document.removeEventListener('cart:updated', this.boundCartUpdated);
    }
    if (this.boundCartRefresh) {
      document.removeEventListener('cart:refresh', this.boundCartRefresh);
    }
  }

  initDraggableScroll() {
    const container = this.querySelector('[data-draggable-scroll]');
    if (!container || container.dataset.draggableInitialized) return;
    
    container.dataset.draggableInitialized = 'true';

    let startX = 0, startY = 0, scrollLeft = 0, isDown = false;

    const onMouseDown = e => {
      isDown = true;
      startX = e.pageX;
      startY = e.pageY;
      scrollLeft = container.scrollLeft;
      container.classList.add('is-dragging');
    };
    
    const onMouseMove = e => {
      if (!isDown) return;
      e.preventDefault();
      container.scrollLeft = scrollLeft + (startX - e.pageX);
    };
    
    const onMouseUpOrLeave = () => {
      isDown = false;
      container.classList.remove('is-dragging');
    };
    
    const onClick = e => {
      const distance = Math.hypot(e.pageX - startX, e.pageY - startY);
      if (distance > 5) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    container.addEventListener('mousedown', onMouseDown);
    container.addEventListener('mousemove', onMouseMove);
    container.addEventListener('mouseup', onMouseUpOrLeave);
    container.addEventListener('mouseleave', onMouseUpOrLeave);
    container.addEventListener('click', onClick, true);

    container.querySelectorAll('img').forEach(img => 
      img.addEventListener('dragstart', e => e.preventDefault())
    );

    const leftArrow = this.querySelector('.promo-block-inner-container-arrow-left');
    const rightArrow = this.querySelector('.promo-block-inner-container-arrow-right');

    function getScrollAmount(promoBlock) {
      const isMiniCart = promoBlock.closest('.drawer__content') !== null;

      if (isMiniCart) {
        // Use smaller scroll amount in minicart
        return window.innerWidth < 556 ? 240 : 300;
      } else {
        // Normal scroll amount on PDP or other pages
        return window.innerWidth < 556 ? 260 : 360;
      }
    }

    if (leftArrow) {
      leftArrow.addEventListener('click', () => {
        const scrollAmount = getScrollAmount(this);
        container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      });
    }

    if (rightArrow) {
      rightArrow.addEventListener('click', () => {
        const scrollAmount = getScrollAmount(this);
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      });
    }
  }

  updatePromoBlock() {
    const discountTitle = this.dataset.discountTitle;
    if (!discountTitle) return Promise.resolve();

    return fetch('/cart.js')
      .then(res => {
        if (!res.ok) throw new Error('Cart fetch failed');
        return res.json();
      })
      .then(cart => {
        this.updatePromoBlockWithCart(cart);
        return cart;
      })
      .catch(err => {
        console.error('Error fetching cart for promo block:', err);
        throw err;
      });
  }

  updatePromoBlockWithCart(cart) {
    const discountTitle = this.dataset.discountTitle;
    if (!discountTitle || !cart) return;

    // Get the titles from data attributes
    const dynamicTitle = this.dataset.dynamicTitle || '';
    const count1Title = this.dataset.count1Title || '';
    const count2Title = this.dataset.count2Title || '';

    let discountCount = 0;
    const discountTitleUpper = discountTitle.toUpperCase();
    const countedDiscounts = new Set();

    // Count cart-level discounts
    (cart.cart_level_discount_applications || []).forEach(discount => {
      if (discount.title && discount.title.toUpperCase().includes(discountTitleUpper)) {
        const discountKey = discount.title + '_' + (discount.target_type || 'cart');
        if (!countedDiscounts.has(discountKey)) {
          countedDiscounts.add(discountKey);
          discountCount++;
        }
      }
    });

    // Count line-level discounts
    (cart.items || []).forEach(item => {
      (item.line_level_discount_allocations || []).forEach(allocation => {
        const title = allocation.discount_application?.title;
        const amount = allocation.amount || 0;
        const itemPrice = item.price || item.original_price || 0;

        if (amount > 0 && title && title.toUpperCase().includes(discountTitleUpper)) {
          const count = itemPrice > 0 ? Math.floor(amount / itemPrice) : 1;
          discountCount += count;
        }
      });
    });

    let isCA = false;
    const host = window.location.hostname.toLowerCase();
    if (host.endsWith('.ca')) {
      isCA = true;
    }
    // Set max and title display
    const maxDiscounts = isCA ? 1 : 2;
    let showCarousel = true;
    let newTitle = '';
    
    // Count how many full-priced items (non-discounted) are in cart
    let fullPricedItemCount = 0;
    let discountedItemCount = 0;
    
    (cart.items || []).forEach(item => {
      // Check if item has promotional discount AND the discount amount is greater than 0
      const hasDiscount = (item.line_level_discount_allocations || []).some(allocation => {
        const title = allocation.discount_application?.title;
        const amount = allocation.amount || 0;
        return title && title.toUpperCase().includes(discountTitleUpper) && amount > 0;
      });
      
      if (hasDiscount) {
        discountedItemCount += item.quantity;
      } else {
        fullPricedItemCount += item.quantity;
      }
    });
    
    // Check if there's an unpaired full-priced item waiting for next discount
    const hasUnpairedItem = fullPricedItemCount > discountedItemCount;
    const arrows = this.querySelectorAll('.promo-block-inner-container-arrow');

    if (discountCount === 0) {
      newTitle = dynamicTitle;
      showCarousel = true;
    } else if (discountCount === 1) {
      // hide carousel and arrows for ca store when 1 discount applied
      if (isCA) {
        showCarousel = false;
        newTitle = count2Title;

        if (arrows.length > 0) {
          arrows.forEach(arrow => {
            arrow.style.display = 'none';
          });
        }
      } else {
        if (hasUnpairedItem) {
          newTitle = dynamicTitle;
        } else {
          newTitle = count1Title;
        }
          showCarousel = true;
        }
    } else if (discountCount >= maxDiscounts) {
      newTitle = count2Title;
      showCarousel = false;
      if (arrows.length > 0) {
        arrows.forEach(arrow => {
          arrow.style.display = 'none';
        });
      }
    }

    // Update UI
    const titleEl = this.querySelector('[data-promo-title]');
    if (titleEl) titleEl.textContent = newTitle;

    // Hide/show carousel based on discount count
    this.querySelectorAll('[data-promo-carousel]').forEach(el => {
      if (showCarousel) {
        // Show carousel
        el.style.removeProperty('display');
        el.style.removeProperty('visibility');
        el.style.removeProperty('height');
        el.style.removeProperty('overflow');
        // show arrows
        const arrows = this.querySelectorAll('.promo-block-inner-container-arrow');
        if (arrows.length > 0) {
          arrows.forEach(arrow => {
            arrow.style.removeProperty('display');
          });
        }
      } else {
        // Hide carousel (when max is reached)
        el.style.display = 'none';
        el.style.visibility = 'hidden';
        el.style.height = '0';
        el.style.overflow = 'hidden';
      }
    });

    this.dataset.discountCount = discountCount;
  }
}

// Register the custom element
if (!window.customElements.get('promo-block')) {
  window.customElements.define('promo-block', PromoBlockElement);
}

// ============================================================================
// END PROMOTIONAL BLOCK
// ============================================================================

document.addEventListener("DOMContentLoaded", function(event) {
    $(".product-form__accordion button").on( "click", function() {
        $(this).closest(".product-form__accordion").toggleClass("active");
    });

  
});

